import Alpine from 'alpinejs';
import gsap from 'gsap';

const DIGITS = '0123456789';
const LOWERCASE_LETTERS = 'abcdefghijklmnopqrstuvwxyz';
const UPPERCASE_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const randomCharacterLike = (character) => {
    let characters = null;

    if (UPPERCASE_LETTERS.includes(character)) {
        characters = UPPERCASE_LETTERS;
    } else if (LOWERCASE_LETTERS.includes(character)) {
        characters = LOWERCASE_LETTERS;
    } else if (DIGITS.includes(character)) {
        characters = DIGITS;
    }

    if (!characters) {
        return character;
    }

    const alternatives = characters.replace(character, '');

    return alternatives[Math.floor(Math.random() * alternatives.length)];
};

const splitTitle = (title) => {
    const words = title.textContent.trim().split(/\s+/);

    title.textContent = '';

    // Keep words intact while each glyph gets a second CSS-rendered row for the vertical roll.
    words.forEach((word, wordIndex) => {
        const wordElement = document.createElement('span');
        wordElement.className = 'teaser-card-grid__item__title-word';

        word.split('\u00AD').forEach((segment, segmentIndex, segments) => {
            const segmentElement = document.createElement('span');
            segmentElement.className = 'teaser-card-grid__item__title-word-segment';

            Array.from(segment).forEach((character) => {
                const letter = document.createElement('span');
                const track = document.createElement('span');
                const glyph = document.createElement('span');

                letter.className = 'teaser-card-grid__item__title-letter';
                track.className = 'teaser-card-grid__item__title-letter-track';
                glyph.className = 'teaser-card-grid__item__title-letter-glyph';
                letter.dataset.character = character;
                glyph.dataset.character = character;
                glyph.textContent = character;

                track.append(glyph);
                letter.append(track);
                segmentElement.append(letter);
            });

            wordElement.append(segmentElement);

            // Keep soft hyphens native so they remain the only break points inside a word.
            if (segmentIndex < segments.length - 1) {
                wordElement.append(document.createTextNode('\u00AD'));
            }
        });

        title.append(wordElement);

        if (wordIndex < words.length - 1) {
            title.append(document.createTextNode(' '));
        }
    });
};

const splitMeta = (meta) => {
    const walker = document.createTreeWalker(meta, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let textNode = walker.nextNode();

    while (textNode) {
        textNodes.push(textNode);
        textNode = walker.nextNode();
    }

    textNodes.forEach((node) => {
        const fragment = document.createDocumentFragment();

        node.textContent.split(/(\s+)/).forEach((part) => {
            if (!part || /^\s+$/.test(part)) {
                fragment.append(document.createTextNode(part));
                return;
            }

            const word = document.createElement('span');
            word.className = 'teaser-card-grid__item__meta-word';

            Array.from(part).forEach((character) => {
                const letter = document.createElement('span');
                letter.className = 'teaser-card-grid__item__meta-letter';
                letter.textContent = character;
                word.append(letter);
            });

            fragment.append(word);
        });

        node.replaceWith(fragment);
    });
};

Alpine.data('teaserCardGrid', () => ({
    observer: null,
    itemListeners: [],

    init() {
        if (
            window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
            document.body.classList.contains('neos-backend')
        ) {
            return;
        }

        const items = Array.from(this.$root.querySelectorAll('[data-teaser-card-grid-item]'));

        items.forEach((item) => {
            const title = item.querySelector('[data-teaser-card-grid-title]');
            const meta = item.querySelector('.teaser-card-grid__item__content .rich-text');

            if (title) {
                splitTitle(title);
            }

            if (meta) {
                splitMeta(meta);
                gsap.set(meta.querySelectorAll('.teaser-card-grid__item__meta-letter'), { opacity: 0 });
            }

            const animateItem = () => this.animateTitle(title);

            item.addEventListener('mouseenter', animateItem);
            item.addEventListener('focusin', animateItem);
            this.itemListeners.push({ item, animateItem });
        });

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    const title = entry.target.querySelector('[data-teaser-card-grid-title]');
                    const meta = entry.target.querySelector('.teaser-card-grid__item__content .rich-text');

                    this.animateTitle(title);
                    this.revealMeta(meta);
                    this.observer.unobserve(entry.target);
                });
            },
            {
                rootMargin: '0px 0px -8% 0px',
                threshold: 0.15,
            }
        );

        items.forEach((item) => this.observer.observe(item));
    },

    animateTitle(title) {
        if (!title) {
            return;
        }

        const tracks = title.querySelectorAll('.teaser-card-grid__item__title-letter-track');
        const glyphs = title.querySelectorAll('.teaser-card-grid__item__title-letter-glyph');

        gsap.killTweensOf(tracks);
        gsap.set(tracks, { yPercent: 0 });
        glyphs.forEach((glyph) => {
            glyph.textContent = randomCharacterLike(glyph.dataset.character);
        });
        gsap.fromTo(
            tracks,
            {
                yPercent: 0,
                x: () => gsap.utils.random(-2, 2),
                rotation: () => gsap.utils.random(-3, 3),
                scaleY: () => gsap.utils.random(0.9, 1.1),
            },
            {
                yPercent: -100,
                x: 0,
                rotation: 0,
                scaleY: 1,
                duration: () => gsap.utils.random(0.28, 0.36),
                delay: () => gsap.utils.random(0, 0.08),
                ease: 'power4.out',
                onComplete: () => {
                    glyphs.forEach((glyph) => {
                        glyph.textContent = glyph.dataset.character;
                    });
                    gsap.set(tracks, { clearProps: 'transform' });
                },
            }
        );
    },

    revealMeta(meta) {
        if (!meta) {
            return;
        }

        const letters = meta.querySelectorAll('.teaser-card-grid__item__meta-letter');

        gsap.killTweensOf(letters);
        gsap.fromTo(
            letters,
            {
                opacity: 0,
            },
            {
                opacity: 1,
                duration: 0.08,
                stagger: 0.045,
                ease: 'power2.out',
                clearProps: 'opacity',
            }
        );
    },

    destroy() {
        this.observer?.disconnect();
        this.itemListeners.forEach(({ item, animateItem }) => {
            item.removeEventListener('mouseenter', animateItem);
            item.removeEventListener('focusin', animateItem);
        });
    },
}));
